import React from 'react';
import { cn } from '@rocco/utils/cn';
import { Separator } from '@rocco/components/separator';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@rocco/components/tabs';
import type { Conversation, PartialConversation } from '../../../types';
import { ChatBoxSettingsSettings } from './ChatBoxSettingsSettings';
import { ChatBoxSettingsMembers } from './ChatBoxSettingsMembers';
import { ChatBoxSettingsButton } from './ChatBoxSettingsButton';

/**
 * The settings interface for the chat box (direct) conversation settings.
 * Contains the conversation settings, and the participants of the conversation.
 * @see {@link ChatBox}
 */
export function ChatBoxSettings({
    className,
    conversation,
    onClose,
}: {
    className?: string;
    conversation: Extract<
        Conversation | PartialConversation,
        { __typename: 'DirectConversation' }
    >;
    onClose?: () => void;
}) {
    return (
        <Tabs defaultValue="settings" className={cn('gap-0', className)}>
            <TabsList className="h-[36px] bg-transparent p-0">
                <ChatBoxSettingsTabsTrigger value="settings">
                    Settings
                </ChatBoxSettingsTabsTrigger>
                <ChatBoxSettingsTabsTrigger value="members">
                    Members
                </ChatBoxSettingsTabsTrigger>
            </TabsList>
            <Separator className="mb-[32px] bg-white" />
            <TabsContent value="settings">
                <ChatBoxSettingsSettings
                    className="size-full"
                    conversation={conversation}
                    onClose={onClose}
                />
            </TabsContent>
            <TabsContent value="members" className="flex flex-col gap-[8px]">
                <ChatBoxSettingsMembers
                    className="min-h-0 flex-1"
                    conversation={conversation}
                />
                <div className="flex items-center justify-end">
                    <ChatBoxSettingsButton onClick={onClose}>
                        Save & Close
                    </ChatBoxSettingsButton>
                </div>
            </TabsContent>
        </Tabs>
    );
}

function ChatBoxSettingsTabsTrigger({
    children,
    className,
    ...props
}: React.ComponentProps<typeof TabsTrigger>) {
    return (
        <TabsTrigger
            className={cn(
                'group relative rounded-none bg-transparent px-[8px] py-[4px] text-[12px] text-black/50 data-[state=active]:bg-transparent data-[state=active]:text-black',
                className
            )}
            {...props}
        >
            {children}
            <div className="absolute inset-x-[-1px] bottom-[-2px] hidden h-[2px] bg-black group-data-[state=active]:block"></div>
        </TabsTrigger>
    );
}
